import React, {createContext, useState, useEffect, useContext} from 'react';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';
import Honeybadger from '@honeybadger-io/js';
import noop from 'lodash/noop';
import cookie from 'js-cookie';
import CoupleContext from './CoupleContext';
import MemberContext from './MemberContext';
import useGetUserState from '../hooks/useGetUserState';
import useUpdateOnboarding from '../hooks/useUpdateOnboarding';
import getMemberToken from '../utils/getMemberToken';
import {RPV_COOKIE_NAME} from '../constants/app';
import useSetRegistryGoal from '../hooks/useSetRegistryGoal';
import useSetRegistryInterests from '../hooks/useSetRegistryInterests';
import useSetWeddingGuestCount from '../hooks/useSetWeddingGuestCount';

const Context = createContext({
  isFetching: true,

  // UserStateAPI fields
  welcomeQuestion: new Set([]),
  hasExistingRegistries: false,
  otherRegistriesQuestion: '',
  registryGoal: '',
  customGoal: '',
  guestCount: '',

  // Rest
  storeUserInput: noop,
  setOtherRegistriesQuestion: noop,
  setWelcomeQuestion: noop,
  setRegistryGoal: noop,
  setCustomGoal: noop,
  setGuestCountQuestion: noop,
  storeRegistryGoal: noop,
  storeRegistryInterests: noop,
  storeWeddingGuestCount: noop,
  validRegistries: [],
});

export default Context;

const validRedirectLocations = new Set(['/', '/shop', '/overview', '/cash']);

const hasOnboardingRedirect = (currentRoute, hasVisitedRegistry) => {
  const token = getMemberToken();
  const rpvCookieValue = cookie.get(RPV_COOKIE_NAME);

  if (!token || token === rpvCookieValue) {
    return false;
  }

  if (currentRoute === '/cash') {
    const queryParams = new URLSearchParams(window.location.search);
    const isExplicitOnboardingRedirect = queryParams.get('onboard');

    return Boolean(isExplicitOnboardingRedirect && !hasVisitedRegistry);
  }

  return !hasVisitedRegistry;
};

export const OnboardingContextProvider = ({children}) => {
  const {registries, fetchingCouple} = useContext(CoupleContext);
  const {memberId} = useContext(MemberContext);

  const [welcomeQuestion, setWelcomeQuestion] = useState(new Set([]));
  const [otherRegistriesQuestion, setOtherRegistriesQuestion] = useState(null);
  const [registryGoal, setRegistryGoal] = useState(null);
  const [guestCount, setGuestCount] = useState(null);
  const [selectedInterestIds, setSelectedInterestIds] = useState([]);
  const [customGoal, setCustomGoal] = useState('');
  const {
    data: userStateData,
    isLoading: isLoadingUserState,
  } = useGetUserState();
  const {mutateAsync: updateUserState} = useUpdateOnboarding();
  const {mutateAsync: updateRegistryGoal} = useSetRegistryGoal();
  const {mutateAsync: updateRegistryInterests} = useSetRegistryInterests();
  const {mutateAsync: updateWeddingGuestCount} = useSetWeddingGuestCount();

  const validRegistries = registries
    .filter(reg => reg?.retailer?.isPartner)
    .sort((a, b) => a.retailer.name.localeCompare(b.retailer.name));

  const hasExistingRegistries = !!validRegistries.length;

  const storeUserInput = async () => {
    try {
      await updateUserState({
        onboarding: {
          hasExistingRegistries,
          otherRegistriesQuestion,
          welcomeQuestion: Array.from(welcomeQuestion),
        },
      });
    } catch (error) {
      Honeybadger.notify(error, 'UpdateUserStateApiError');
    }
  };

  const storeRegistryGoal = async () => {
    try {
      await updateRegistryGoal({goalId: Number(registryGoal), customGoal});
    } catch (error) {
      Honeybadger.notify(error, 'UpdateRegistryGoalError');
    }
  };

  const storeRegistryInterests = async () => {
    try {
      await updateRegistryInterests({interestIds: selectedInterestIds});
    } catch (error) {
      Honeybadger.notify(error, 'UpdateRegistryInterests');
    }
  };

  const storeWeddingGuestCount = async () => {
    try {
      await updateWeddingGuestCount({memberId, guestCount});
    } catch (error) {
      Honeybadger.notify(error, 'UpdateWeddingGuestCount');
    }
  };

  const loc = useLocation();
  const [initialLocation, setInitialLocation] = useState();
  const [shouldSeeOnboarding, setShouldSeeOnboarding] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    setInitialLocation(loc.pathname);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!initialLocation) {
      return;
    }

    if (validRedirectLocations.has(initialLocation)) {
      if (!isLoadingUserState) {
        const isShowingOnboarding = hasOnboardingRedirect(
          initialLocation,
          userStateData?.user?.hasVisitedRegistry,
        );

        setShouldSeeOnboarding(isShowingOnboarding);
      }
    }

    setIsFetching(false);
  }, [initialLocation, isLoadingUserState, userStateData]);

  return (
    <Context.Provider
      value={{
        shouldSeeOnboarding,
        isFetching,
        isFetchingRegistries: fetchingCouple,
        welcomeQuestion,
        hasExistingRegistries,
        otherRegistriesQuestion,
        guestCount,
        setOtherRegistriesQuestion,
        setWelcomeQuestion,
        setGuestCount,
        storeWeddingGuestCount,
        storeUserInput,
        validRegistries,
        registryGoal,
        setRegistryGoal,
        storeRegistryGoal,
        customGoal,
        setCustomGoal,
        selectedInterestIds,
        setSelectedInterestIds,
        storeRegistryInterests,
      }}>
      {children}
    </Context.Provider>
  );
};

OnboardingContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
